import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Wrench, Sparkles, ArrowRight, Check, Mail, X, Loader2, Award } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG = 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images';

const PLAN_IMAGES = {
  'platinum-prestige': `${IMG}/a64af9c5f008cf620b3183281a6aef05b19ae5264aace13380bd778300d20635.jpeg`,
  'maintenance-standard': `${IMG}/42db314d080749461556c6323925640b0e02e0cdb27b5f1f7bd51c5d177f8de4.jpeg`,
  'maintenance-premium': `${IMG}/36b924b8d37656fed56c69f2b34eec21595b6564360550748db91f3972e66b20.jpeg`,
  'maintenance-elite': `${IMG}/e8e4f1c42ddb67141e56f52fd37b506f79c3b790a874d2accec26901b8f1fd56.jpeg`,
  'cleaning-essential': `${IMG}/54b448096f67e5d519d7091e44856dd2603fdb728f4dd4259d27b66aa17f4e02.jpeg`,
  'cleaning-premium': `${IMG}/99db79724b255f7b6ad4ff01422849a3b085ec9635469b00136e2f83f1bc88f5.jpeg`,
  'cleaning-elite': `${IMG}/1da3127644a07d755188454ad110eac5ac5543e49e4d4c1f3db3f110c23e44d9.jpeg`,
  'cleaning-concierge': `${IMG}/d35b1d982ff6a6419133d80d1e48210275b61a33b73b930141d6eb65699b10fd.jpeg`,
};
const FEATURED_IDS = ['maintenance-premium', 'cleaning-elite'];

function InquiryModal({ pkg, onClose }) {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        message: `[Package Inquiry — ${pkg}] ${form.message || 'Please contact me with more information.'}`,
      });
      setSent(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-testid="inquiry-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#111113] border border-[#D4AF37]/30 rounded-sm max-w-md w-full p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="close-inquiry-modal"><X size={18} /></button>
        {sent ? (
          <div className="text-center py-6" data-testid="inquiry-success">
            <div className="w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-5"><Check size={26} className="text-black" /></div>
            <h3 className="font-playfair text-xl text-white mb-2">Inquiry Sent!</h3>
            <p className="text-white/40 font-manrope text-sm">Our team will contact you shortly about the {pkg}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <h3 className="font-playfair text-xl text-white mb-1">Inquire About This Plan</h3>
              <p className="text-[#D4AF37] font-manrope text-xs uppercase tracking-widest">{pkg}</p>
            </div>
            <input type="text" required placeholder="Full Name" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-white/25 font-manrope text-sm" data-testid="inquiry-name-input" />
            <input type="email" required placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-white/25 font-manrope text-sm" data-testid="inquiry-email-input" />
            <input type="tel" required placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-white/25 font-manrope text-sm" data-testid="inquiry-phone-input" />
            <textarea rows={3} placeholder="Questions or details about your theater (optional)" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-white/25 font-manrope text-sm resize-none" data-testid="inquiry-message-input" />
            <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest py-3.5 rounded-sm hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50" data-testid="submit-inquiry-btn">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <><Mail size={14} /> Send Inquiry</>}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

function PlanCard({ plan, index, onInquire }) {
  const featured = FEATURED_IDS.includes(plan.id);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className={`bg-[#111113] border rounded-sm overflow-hidden flex flex-col hover:-translate-y-1 transition-transform duration-300 ${featured ? 'border-[#D4AF37]/40' : 'border-white/5'}`}
      data-testid={`plan-${plan.id}`}
    >
      <div className="relative h-48">
        <img src={PLAN_IMAGES[plan.id] || PLAN_IMAGES['maintenance-standard']} alt={plan.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111113] to-transparent" />
        {featured && (
          <span className="absolute top-4 right-4 bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest font-manrope font-bold px-3 py-1.5 rounded-sm">Most Popular</span>
        )}
        {plan.visits_per_year > 0 && (
          <span className="absolute bottom-3 left-4 bg-black/70 backdrop-blur-sm border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] uppercase tracking-widest font-manrope font-semibold px-3 py-1.5 rounded-full">
            {plan.visits_per_year} visit{plan.visits_per_year > 1 ? 's' : ''} / year
          </span>
        )}
      </div>
      <div className="p-7 flex flex-col flex-1">
        <h3 className="font-playfair text-xl text-white mb-2">{plan.name}</h3>
        <p className="text-white/40 font-manrope text-xs leading-relaxed mb-5">{plan.description}</p>
        <ul className="space-y-2.5 mb-8 flex-1">
          {(plan.features || []).map((f, fi) => (
            <li key={fi} className="flex gap-2.5 text-white/50 font-manrope text-xs leading-relaxed">
              <Check size={13} className="text-[#D4AF37] flex-shrink-0 mt-0.5" /> {f}
            </li>
          ))}
        </ul>
        <button onClick={() => onInquire(plan.name)} className="w-full flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] font-manrope font-semibold text-xs uppercase tracking-widest py-3.5 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-all duration-300" data-testid={`inquire-${plan.id}`}>
          <Mail size={13} /> Inquire via Email
        </button>
      </div>
    </motion.div>
  );
}

export default function ServicesPage() {
  const [inquiryPkg, setInquiryPkg] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then(r => {
        const arr = Object.values(r.data).filter(p => p.active !== false);
        arr.sort((a, b) => (a.order || 0) - (b.order || 0));
        setPlans(arr);
      })
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const platinum = plans.find(p => p.category === 'warranty');
  const maintenancePlans = plans.filter(p => p.category === 'maintenance');
  const cleaningPlans = plans.filter(p => p.category === 'cleaning');

  return (
    <div className="min-h-screen pt-24" data-testid="services-page">
      {/* Hero */}
      <section className="py-20 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">Warranty, Maintenance & Cleaning</p>
            <h1 className="font-playfair text-4xl lg:text-5xl text-white mb-4">
              Peace of Mind, <span className="text-[#D4AF37]">You're Covered</span>
            </h1>
            <p className="text-white/40 font-manrope text-lg max-w-2xl leading-relaxed">
              We stand behind the quality of our craftsmanship and materials. Every Dream Room Theater installation includes coverage for defects in materials and workmanship for the term of your plan. From AV performance to the finest design details, our plans ensure your investment remains secure.
            </p>
          </motion.div>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-32" data-testid="services-loading">
          <Loader2 size={28} className="text-[#D4AF37] animate-spin" />
        </div>
      ) : (
        <>
          {/* Warranty & Maintenance */}
          <section className="py-20" data-testid="platinum-prestige-section">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 rounded-sm bg-[#D4AF37]/10 flex items-center justify-center">
                  <Shield size={18} className="text-[#D4AF37]" />
                </div>
                <h2 className="font-playfair text-2xl lg:text-3xl text-white">Warranty & Maintenance</h2>
              </motion.div>

              {platinum && (
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-gradient-to-br from-[#1a1a1a] to-[#111113] border border-[#D4AF37]/25 rounded-sm overflow-hidden mb-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="relative min-h-[280px] lg:min-h-full">
                      <img src={PLAN_IMAGES['platinum-prestige']} alt={platinum.name} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-transparent to-[#141414]/60 lg:from-transparent lg:to-[#141414]" />
                    </div>
                    <div className="p-8 lg:p-12">
                      <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm px-3 py-1.5 mb-5">
                        <Award size={12} className="text-[#D4AF37]" />
                        <span className="text-[#D4AF37] text-[10px] uppercase tracking-[0.2em] font-manrope font-semibold">Flagship Care Plan</span>
                      </div>
                      <h3 className="font-playfair text-2xl lg:text-3xl text-white mb-2">{platinum.name}</h3>
                      <p className="text-[#D4AF37]/80 font-manrope text-sm italic mb-8">{platinum.description}</p>
                      <div className="space-y-3 mb-8">
                        {(platinum.features || []).map((f, i) => (
                          <div key={i} className="flex gap-3">
                            <Check size={15} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                            <p className="text-white/60 font-manrope text-sm leading-relaxed">{f}</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-white/40 font-manrope text-xs leading-relaxed mb-8 border-l-2 border-[#D4AF37]/40 pl-4">
                        <span className="text-[#D4AF37] font-semibold">Why Platinum Prestige?</span> Your theater not only remains in pristine, like-new condition — it evolves with the latest advancements in cinematic technology. Every detail is protected, so you enjoy uninterrupted, world-class entertainment without compromise.
                      </p>
                      <button onClick={() => setInquiryPkg(platinum.name)} className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#C5A059] transition-all duration-300" data-testid="inquire-platinum-prestige">
                        <Mail size={14} /> Inquire via Email
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-sm bg-[#D4AF37]/10 flex items-center justify-center">
                    <Wrench size={18} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="font-playfair text-xl lg:text-2xl text-white">Maintenance Packages</h3>
                </div>
                <p className="text-white/40 font-manrope max-w-3xl leading-relaxed text-sm">
                  Professional scheduled maintenance that keeps every system in your theater calibrated, protected, and performing like opening night.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="maintenance-section">
                {maintenancePlans.map((plan, i) => (
                  <PlanCard key={plan.id} plan={plan} index={i} onInquire={setInquiryPkg} />
                ))}
              </div>
            </div>
          </section>

          {/* Cleaning Services */}
          <section className="py-20 bg-[#0a0a0b]" data-testid="cleaning-section">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-sm bg-[#D4AF37]/10 flex items-center justify-center">
                    <Sparkles size={18} className="text-[#D4AF37]" />
                  </div>
                  <h2 className="font-playfair text-2xl lg:text-3xl text-white">Cleaning Services</h2>
                </div>
                <p className="text-white/40 font-manrope max-w-3xl leading-relaxed">
                  Theater care for long-lasting luxury — performed by our team of specialized luxury cleaners. Your home theater is an investment in comfort, design, and technology. To keep it in pristine condition year after year, Dream Room Theaters offers dedicated cleaning plans performed by trained professionals who understand the unique needs of luxury theater spaces.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {cleaningPlans.map((plan, i) => (
                  <PlanCard key={plan.id} plan={plan} index={i} onInquire={setInquiryPkg} />
                ))}
              </div>

              <p className="text-white/30 font-manrope text-sm text-center mt-12">
                <Wrench size={13} className="inline mr-2 text-[#D4AF37]" />
                Existing clients: plans can be purchased directly in the <Link to="/portal" className="text-[#D4AF37] underline underline-offset-4" data-testid="services-portal-link">Client Portal</Link> with monthly billing or a 20% annual-contract discount.
              </p>
            </div>
          </section>
        </>
      )}

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-playfair text-3xl text-white mb-4">Ready to Get Started?</h2>
          <p className="text-white/40 font-manrope max-w-lg mx-auto mb-8">
            Book a consultation and our team will help you choose the right care plan for your theater.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book" className="inline-flex items-center gap-3 bg-[#D4AF37] text-black font-manrope font-bold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#C5A059] transition-all duration-300" data-testid="services-book-cta">
              Book a Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/packages" className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-manrope font-semibold text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-all duration-300">
              View Theater Packages
            </Link>
          </div>
        </div>
      </section>

      {inquiryPkg && <InquiryModal pkg={inquiryPkg} onClose={() => setInquiryPkg(null)} />}
    </div>
  );
}
